import { hasValue, type Maybe, sortBy, uniqueBy } from '@vrplatform/utils';
import { ensure } from '@vrplatform/utils';
import type {
  HasuraClient,
  Query,
  listing,
  owner_statement_bool_exp,
  owner_statement_line_bool_exp,
  owner_statement_status_enum,
} from '@vrplatform/graphql';
import { whereListingIsExcludedSetting } from '../queries';
import {
  getAccountDescription,
  getListingAddress,
  getListingStatus,
  getOwnerAddress,
  getTenantAddress,
} from '../utils';
import type { Statement, StatementLine } from './_types';

export interface StatementInput {
  teamId: string;
  groupedBy:
    | 'groupByReservation'
    | 'groupByMonth'
    | 'groupByListing'
    | 'groupByBookingChannel';
  dashboard: 'propertyManager' | 'owner';
  statementIds?: string[];
  ownerId?: string;
  vendorId?: string;
  listingId?: string;
  status?: owner_statement_status_enum[];
  startAt?: string | null;
  endAt?: string | null;
}

export const useStatementExportData = async (
  client: HasuraClient,
  args: StatementInput
) => {
  const statements = await client.query((query) => {
    const where: owner_statement_bool_exp = args.statementIds
      ? { id: { _in: args.statementIds } }
      : {
          listing: args.listingId
            ? {
                id: args.listingId
                  ? {
                      _eq: args.listingId,
                    }
                  : undefined,
              }
            : undefined,
          startAt: {
            _gte: args.startAt,
            _lte: args.endAt,
          },
          owners: args.ownerId
            ? {
                newOwnerId: { _eq: args.ownerId },
                vendorSourceId: args.vendorId
                  ? { _eq: args.vendorId }
                  : undefined,
              }
            : {
                role: { _eq: 'owner' },
                vendorSourceId: args.vendorId
                  ? { _eq: args.vendorId }
                  : undefined,
              },
          tenantId: {
            _eq: args.teamId,
          },
          status: args.status ? { _in: args.status as any } : undefined,
        };

    return getStatementQuery(query, where, {
      teamId: args.teamId,
    });
  });

  const template = sortBy(
    statements?.map((st) => st.template),
    (x) => x?.version || 0
  ).reverse()[0];

  const statementLines = statements.flatMap((statement) => statement.lines);

  return {
    statements,
    template,
    statementLines,
  };
};

const getListing = (listing: Maybe<listing>): StatementLine['listing'] => {
  const base = {
    id: listing?.id,
    name: listing?.calculatedTitle || '-',
    address: getListingAddress(listing, { format: 'owner_statement' }).full,
  };

  if (!base.id) return undefined;

  return base as NonNullable<StatementLine['listing']>;
};

export const getStatementQuery = (
  q: Query,
  where: owner_statement_bool_exp,
  args: {
    teamId: string;
    statementAutomationId?: string;
  },
  filter?: {
    lines: owner_statement_line_bool_exp;
  }
): Statement[] => {
  const ownerStatements = q.ownerStatements({
    where,
    order_by: [{ startAt: 'asc' }],
  });

  const whereLines = filter?.lines || {};

  const apps = q
    .app({
      where: {
        connections: {
          tenantId: { _eq: args.teamId },
        },
      },
      order_by: [{ id: 'asc' }],
    })
    .map((app) => ({
      id: app.id,
      iconRound: app.iconRound,
    }));

  const listingRels = q
    .listings({
      where: {
        tenantId: { _eq: args.teamId },
        reservations: {
          ownerStatementLines: {
            ownerStatement: where,
          },
        },
      },
    })
    .map((lc) => getListing(lc));

  const listingConnections = q
    .listingConnections({
      where: {
        tenantId: { _eq: args.teamId },
        reservations: {
          ownerStatementLines: {
            ownerStatement: where,
          },
        },
      },
      distinct_on: ['listingId'],
    })
    .map((lc) => getListing(lc.listing));

  const listings = uniqueBy(
    [...listingRels, ...listingConnections].filter(hasValue),
    'id'
  );

  return ownerStatements.map((statement) => {
    const disabledAutomation = !!statement.listing?.settingsRight({
      where: whereListingIsExcludedSetting({
        automationIds: args?.statementAutomationId
          ? [args.statementAutomationId]
          : [],
        listingId: undefined,
      }),
      limit: 1,
      order_by: [{ updated_at: 'desc' }],
    })[0]?.id;

    const listingStatus = getListingStatus(statement.listing);

    const uniqueReservations = statement
      .lines({
        where: {
          _and: [whereLines, { reservation: {} }],
        },
        order_by: [
          {
            reservationId: 'asc_nulls_last',
          },
        ],
        distinct_on: ['reservationId'],
      })
      .map((line) => {
        const reservation = line.reservation!;

        const listingConnectionListingId =
          reservation?.listingConnection?.listingId;
        const listingId = reservation?.listingId;
        const appId = reservation.connection?.appId;

        const isCancelled = reservation?.status === 'cancelled';
        const nights = reservation?.nights;

        const listing = listings.find(
          (lc) => lc.id === listingConnectionListingId || lc.id === listingId
        );

        return {
          id: reservation.id,
          currency: reservation.currency,
          checkIn: reservation.checkIn,
          checkOut: reservation.checkOut,
          nights: isCancelled ? 0 : nights,
          status: reservation.status,
          pmsReferenceCode: reservation.pmsReferenceCode,
          confirmationCode: reservation.confirmationCode,
          connectionIcon: apps.find((a) => a.id === appId)?.iconRound,
          guestName: reservation.guestName,
          guests: reservation.guests,
          bookingChannel: reservation.channel?.uniqueRef,
          listing,
        };
      });

    const lines = statement
      .lines({
        where: whereLines,
        order_by: [{ reservation: { checkIn: 'asc_nulls_last' } }],
      })
      .map<Statement['lines'][0]>((line) => {
        const reservationId = line.reservationId;
        const reservation = uniqueReservations.find(
          (res) => res.id === reservationId
        );
        return {
          id: line.id,
          statementId: statement.id,
          centTotal: line.centTotal ?? 0,
          description: line.customDescription?.trim() || line.description, // customDescription => are users able to overwrite journal entry description?
          currency: line.currency,
          date: line.date!,
          isOwnerPayout: line.isOwnerPayout, // always gets filtered out => should UI just not query it???
          externalLink: line.externalLink, // are we going to have this?
          role: line.role || undefined, // needed for distinguishing bill => what goes into summary
          metadata: line.metadata(), // needed for possible "metadata" column in net rev table
          listing: getListing(statement?.listing),
          group: {
            // this is the account
            id: line.group?.id,
            remoteId: line.group?.remoteId,
            type: line.group?.type,
            description: line.group?.description,
            details: getAccountDescription(line.group),
          },
          reservation,
        };
      });

    const statementOwners = statement
      .owners({
        where: {
          newOwnerId: { _is_null: false },
        },
        order_by: [
          {
            newOwner: {
              name: 'asc_nulls_last',
            },
          },
        ],
      })
      .map((ship) => {
        return {
          id: ship.id,
          split: ship.split,
          owner: {
            id: ship.newOwnerId!,
            email: ship.newOwner?.email,
            firstName: ship.newOwner?.firstName,
            lastName: ship.newOwner?.name,
            companyName: ship.newOwner?.name,
            address: getOwnerAddress(ship.newOwner!, {
              format: 'owner_statement',
            }).full,
            phone: ship.newOwner?.phone,
          },
        };
      });

    return ensure<Statement>({
      id: statement.id,
      startAt: statement.startAt,
      status: statement.status ?? 'draft',
      centBalanceEnd: statement.centBalanceEnd || 0,
      centBalanceStart: statement.centBalanceStart || 0,
      centPayedOut: statement.centPayedOut || 0,
      centTotal: statement.centTotal || 0,
      currency: statement.currency,
      template: {
        id: statement.template!.id,
        data: statement.template?.data(),
        version: statement.template?.version,
      },
      tenant: {
        id: statement.tenantId,
        name: statement.tenant?.name,
        companyName: statement.tenant?.companyName,
        logo: statement.tenant?.logo,
        taxId: statement?.tenant?.companyTaxCode,
        address: getTenantAddress(statement.tenant, {
          format: 'owner_statement',
        }).full,
        phone: statement?.tenant?.supportPhone,
        email: statement.tenant?.supportEmail,
      },
      statementOwners,
      lines,
      listing: {
        // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: listing should always be defined
        id: statement.listing?.id!,
        name: statement.listing?.calculatedTitle || '-',
        address: getListingAddress(statement.listing, {
          format: 'owner_statement',
        }).full,
        disabled:
          disabledAutomation ||
          listingStatus === 'inactive' ||
          listingStatus === 'pmsDisabled',
      },
    });
  });
};
